/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserProgramContext } from "../../context/user-program/user-program";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
  GridSearchIcon,
  deDE,
} from "@mui/x-data-grid";
import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Button,
  Modal,
  Menu,
  MenuItem,
} from "@mui/material";

import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import { useDebounce } from "../../hooks/useDebounce";
import { getUsersPatients, editPatient } from "../../api/users-patients";
import { Navbar } from "../styled/nav-bar";

interface UserData {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

interface PatientRowData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

export const SectionClients: FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(
    searchParams.get("filter") || ("" as string)
  );
  const { role } = useContext(UserProgramContext);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [patientToEdit, setPatientToEdit] = useState<PatientRowData | null>(
    null
  );
  const [editedPatient, setEditedPatient] = useState<PatientRowData | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [pageState, setPageState] = useState({
    loading: true,
    currentPage: searchParams.get("page")
      ? Number(searchParams.get("page"))
      : 1,
    perPage: 10,
    totalUsers: 0,
    totalPages: 0,
  });

  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setEditedPatient(patientToEdit);
  }, [patientToEdit]);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    patient: PatientRowData
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setPatientToEdit(patient);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setPatientToEdit(null);
  };

  const handleSaveChanges = async () => {
    if (!editedPatient) return;

    try {
      await editPatient(editedPatient?.id, editedPatient);
    } catch (error) {
      console.error("Error saving changes:", error);
    }
    const updatedUsers = users.map((user) => {
      if (user._id === editedPatient.id) {
        return {
          ...user,
          firstName: editedPatient.firstName,
          lastName: editedPatient.lastName,
          email: editedPatient.email,
          phone: editedPatient.phone,
        };
      }
      return user;
    });
    setUsers(updatedUsers);
    handleCloseModal();
  };

  const handleModalFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    if (editedPatient) {
      setEditedPatient({ ...editedPatient, [name]: value });
    }
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 100, hideable: true },
    { field: "firstName", headerName: "Vorname", flex: 1 },
    { field: "lastName", headerName: "Nachname", flex: 1 },
    { field: "phone", headerName: "Telefon", flex: 1 },
    { field: "email", headerName: "E-Mail", flex: 1 },
    {
      field: "patient",
      headerName: "Detail",
      sortable: false,
      filterable: false,
      hideable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Button
            onClick={(event) =>
              handleMenuClick(event, params.row as PatientRowData)
            }
          >
            <MoreHorizOutlinedIcon sx={{ color: "black" }} />
          </Button>
        </Box>
      ),
    },
  ];

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const onRowClick: GridEventListener<"rowClick"> = (event) => {
    const { id } = event.row;
    navigate(`/patients/${id}`);
  };

  const getColumns = () => {
    if (isMobile) {
      return columns.map((column) => ({ ...column, flex: 0, wirth: 150 }));
    } else {
      return columns;
    }
  };

  const debounceValue = useDebounce(searchValue, 500);

  const navigateToPage = (currentPage: number, debounceValue: string) => {
    if (currentPage >= 1) {
      navigate(`/patients?page=${currentPage}`, { replace: true });
    }

    if (debounceValue.length > 0) {
      navigate(
        `/patients?page=${pageState.currentPage}&filter=${debounceValue}`,
        { replace: true }
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setPageState((prevPageState) => ({ ...prevPageState, loading: true }));
      try {
        const data = await getUsersPatients({
          perPage: pageState.perPage,
          currentPage: pageState.currentPage,
          searchValue: debounceValue,
        });

        setUsers(data.users);

        setPageState((prevPageState) => ({
          ...prevPageState,
          totalUsers: data.totalUsers,
          totalPages: data.totalPages,
          loading: false,
        }));

        navigateToPage(pageState.currentPage, debounceValue);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [pageState.perPage, pageState.currentPage, debounceValue]);

  const mapPatientsToRows = () =>
    users.map(({ _id, firstName, lastName, email, phone }) => ({
      id: _id,
      firstName,
      lastName,
      email,
      phone,
    }));

  const rows = mapPatientsToRows();

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      spacing={4}
      pt={2}
      pr={2}
      pl={2}
      pb={5}
    >
      <Navbar />

      <Box
        sx={{
          marginTop: "2em",
          marginRight: "2em",
          marginLeft: "2em",
          width: "80%",
        }}
      >
        <Grid container spacing={4} p={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" fontWeight="bold">
              Patientenliste
            </Typography>
          </Grid>

          <Grid item xs={12} sm={3}>
            {/* Uncomment the following lines if you want to add a date picker in the future */}
            {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker label="Geburtsdatum suchen" />
            {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker label="Geburtsdatum suchen" />
            </LocalizationProvider> */}
          </Grid>

          <Grid item xs={12} sm={3} justifyContent="end">
            <TextField
              fullWidth
              value={searchValue}
              label="Suche"
              variant="standard"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GridSearchIcon />
                  </InputAdornment>
                ),
              }}
              onInput={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} sx={{ width: "100%", cursor: "pointer" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={getColumns()}
              rowCount={pageState.totalUsers}
              loading={pageState.loading}
              rowsPerPageOptions={[5, 10, 15]}
              pagination
              page={pageState.currentPage - 1}
              pageSize={pageState.perPage}
              paginationMode="server"
              onPageChange={(newPage) =>
                setPageState({ ...pageState, currentPage: newPage + 1 })
              }
              onPageSizeChange={(newPageSize) =>
                setPageState({ ...pageState, perPage: newPageSize })
              }
              onRowClick={onRowClick}
              checkboxSelection
              columnVisibilityModel={{
                id: false,
              }}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
            />
          </Grid>
        </Grid>
      </Box>
      {role === "admin" && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEdit}>Bearbeiten</MenuItem>
        </Menu>
      )}

      <Modal open={isEditModalOpen} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2">
            Patient bearbeiten
          </Typography>
          {editedPatient && (
            <Box component="form" noValidate autoComplete="off">
              <TextField
                margin="normal"
                fullWidth
                label="Vorname"
                name="firstName"
                value={editedPatient.firstName}
                onChange={handleModalFieldChange}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Nachname"
                name="lastName"
                value={editedPatient.lastName}
                onChange={handleModalFieldChange}
              />
              <TextField
                margin="normal"
                fullWidth
                label="E-Mail"
                name="email"
                value={editedPatient.email}
                onChange={handleModalFieldChange}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Telefon"
                name="phone"
                value={editedPatient.phone}
                onChange={handleModalFieldChange}
              />
              <Grid container justifyContent="flex-end" spacing={1} pt={2}>
                <Grid item>
                  <Button onClick={handleCloseModal}>Abbrechen</Button>
                </Grid>
                <Grid item>
                  <Button onClick={handleSaveChanges} variant="contained">
                    Speichern
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Modal>
    </Grid>
  );
};
